import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatSpan } from "@/lib/campaign";
import { isCurrentStage, stageSlugs, stageText } from "@/lib/contacts";
import {
  DISPOSITION_CATEGORIES, DISPOSITION_KINDS, DISPOSITION_SLUG_RE, LABEL_MAX, NEW_DISPOSITION,
  categoryTerm, dispositionChanges, dispositionValues, kindTerm, minutesProblem, minutesToSeconds,
  newDispositionPayload, nextSortOrder, spanDefaultText, spanField,
} from "@/lib/dispositions";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useCampaigns, useCreateDisposition, useUpdateDisposition } from "@/lib/queries";
import type { Disposition } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Add an outcome to the catalog, or edit one (ADR-017).
 *
 * The slug is typed once: the contract's `disposition.set`, the CRM's tags and the reports all carry
 * it, so core-api has no way to rename one. The kind is picked from the four the dialer acts on, each
 * saying what it does, and the one span that kind waits on is asked for in minutes, empty for its
 * default. An edit sends only what changed (`dispositionChanges`), so a span saved in odd seconds is
 * never rounded by a label being fixed. Where it sits in the list is moved on the page, not typed
 * here: a new one goes last.
 */

/** Radix's Select cannot hold `""`, so "no category" needs a value of its own. */
const NONE = "none";

const schema = z
  .object({
    slug: z
      .string()
      .min(1, "Required")
      .regex(DISPOSITION_SLUG_RE, "Lowercase letters, digits, underscore and hyphen, up to 63"),
    label: z
      .string()
      .trim()
      .min(1, "Required")
      .max(LABEL_MAX, `At most ${LABEL_MAX} characters`),
    kind: z.string(),
    category: z.string(),
    backoff_minutes: z.string(),
    hold_minutes: z.string(),
    enlist_campaign_id: z.string(),
    requires_note: z.boolean(),
    stage: z.string(),
  })
  .superRefine((values, ctx) => {
    // core-api refuses this pair (ADR-056); the form says why first, on the field that can be
    // changed rather than on the category the admin probably means to keep.
    if (values.category === "compliance" && values.enlist_campaign_id) {
      ctx.addIssue({
        code: "custom",
        path: ["enlist_campaign_id"],
        message: "A compliance outcome takes the lead off every list, so it can add them to none",
      });
    }
    // Only the span the kind waits on is sent, so only that one can hold the save up.
    const span = spanField(values.kind);
    const field = span === "backoff" ? "backoff_minutes" : span === "hold" ? "hold_minutes" : null;
    const problem = field ? minutesProblem(values[field]) : null;
    if (field && problem) ctx.addIssue({ code: "custom", path: [field], message: problem });
  });
type FormValues = z.infer<typeof schema>;

export function DispositionFormDialog({
  open, onOpenChange, disposition, tenantId, catalog,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  disposition?: Disposition;
  /** Whose catalog a new one joins. Sent only by a superadmin, who has no tenant of their own. */
  tenantId: number | null;
  /** The catalog it joins, retired rows included, so a new one goes after the last. */
  catalog: readonly Disposition[];
}) {
  const { user: actor } = useAuth();
  const toast = useToast();
  const create = useCreateDisposition();
  const update = useUpdateDisposition();
  // Where an outcome may hand the lead (ADR-056): this tenant's campaigns, which is what core-api
  // will accept. Only fetched while the dialog is open.
  const { data: campaigns } = useCampaigns(
    tenantId ? { tenant_id: tenantId } : {},
    { enabled: open },
  );
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: NEW_DISPOSITION,
  });
  const { errors } = form.formState;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(disposition ? dispositionValues(disposition) : NEW_DISPOSITION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, disposition]);

  const values = form.watch();
  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    form.setValue(key, value as never, { shouldDirty: true, shouldValidate: true });
  const span = spanField(values.kind);

  /** The hint under a span field: what it comes to, or what empty leaves it to. */
  const spanHint = (text: string) => {
    const seconds = minutesProblem(text) === null ? minutesToSeconds(text) : null;
    return seconds
      ? `Minutes. That is ${formatSpan(seconds)}.`
      : `Minutes. ${spanDefaultText(values.kind, disposition?.backoff_seconds ?? null)}`;
  };

  const onSubmit = form.handleSubmit(async (submitted) => {
    setFormError(null);
    try {
      if (disposition) {
        const changes = dispositionChanges(submitted, disposition);
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({ id: disposition.id, ...changes });
        }
        toast.success("Disposition saved", submitted.label);
      } else {
        await create.mutateAsync({
          ...newDispositionPayload(submitted, nextSortOrder(catalog)),
          ...(actor?.role === "superadmin" && tenantId !== null ? { tenant_id: tenantId } : {}),
        });
        toast.success("Disposition added", submitted.label);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the disposition.");
    }
  });

  const knownCategory = (DISPOSITION_CATEGORIES as readonly string[]).includes(values.category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{disposition ? "Edit disposition" : "New disposition"}</DialogTitle>
          <DialogDescription>
            {disposition
              ? "What reps see it called, and what it does to the lead."
              : "An outcome a rep can choose after a call. It goes to the end of the list; move it on the page."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Label" htmlFor="disposition-label" required error={errors.label?.message}>
                <Input
                  autoComplete="off"
                  placeholder="Referred out"
                  invalid={Boolean(errors.label)}
                  {...form.register("label")}
                />
              </Field>
              <Field
                label="Slug"
                htmlFor="disposition-slug"
                required={!disposition}
                error={errors.slug?.message}
                hint={
                  disposition
                    ? "Fixed. The disposition.set event, the CRM's tags and the reports carry it."
                    : "What the disposition.set event, the CRM's tags and the reports carry. It cannot be changed later."
                }
              >
                <Input
                  // Read-only rather than disabled: a disabled field drops out of the submitted values.
                  readOnly={Boolean(disposition)}
                  className={cn("font-mono", disposition && "bg-surface-sunken text-text-muted")}
                  autoComplete="off"
                  placeholder="referred_out"
                  invalid={Boolean(errors.slug)}
                  {...form.register("slug")}
                />
              </Field>
            </div>

            <fieldset className="min-w-0">
              <legend className="mb-2 text-xs font-medium text-text">What it does to the lead</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {DISPOSITION_KINDS.map((kind) => (
                  <Choice
                    key={kind}
                    name="disposition-kind"
                    checked={values.kind === kind}
                    onSelect={() => set("kind", kind)}
                    title={kindTerm(kind).label}
                    description={kindTerm(kind).description}
                  />
                ))}
              </div>
            </fieldset>

            {span === "backoff" && (
              <Field
                key="backoff"
                label={values.kind === "callback" ? "Call back after" : "Try again after"}
                htmlFor="disposition-backoff"
                error={errors.backoff_minutes?.message}
                hint={spanHint(values.backoff_minutes)}
              >
                <Input
                  className="sm:w-40"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Default"
                  invalid={Boolean(errors.backoff_minutes)}
                  {...form.register("backoff_minutes")}
                />
              </Field>
            )}
            {span === "hold" && (
              <Field
                key="hold"
                label="Hold for"
                htmlFor="disposition-hold"
                error={errors.hold_minutes?.message}
                hint={spanHint(values.hold_minutes)}
              >
                <Input
                  className="sm:w-40"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Default"
                  invalid={Boolean(errors.hold_minutes)}
                  {...form.register("hold_minutes")}
                />
              </Field>
            )}

            <Field
              label="Category"
              htmlFor="disposition-category"
              hint={categoryTerm(values.category)?.description ?? "How reports group it."}
            >
              <Select
                value={values.category || NONE}
                onValueChange={(v) => set("category", v === NONE ? "" : v)}
              >
                <SelectTrigger id="disposition-category" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {DISPOSITION_CATEGORIES.map((category) => {
                    const term = categoryTerm(category);
                    return (
                      <SelectItem key={category} value={category} description={term?.description}>
                        {term?.label ?? category}
                      </SelectItem>
                    );
                  })}
                  {values.category && !knownCategory && (
                    <SelectItem value={values.category}>{values.category}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="Stage"
              htmlFor="disposition-stage"
              hint={
                values.category === "compliance"
                  ? "A compliance outcome puts the lead in Do not call whatever this says."
                  : values.stage
                    ? stageText(values.stage).description
                    : "The stage this outcome puts the lead in. Leave it to keep the stage (Team call, Firm question)."
              }
            >
              <Select value={values.stage || NONE} onValueChange={(v) => set("stage", v === NONE ? "" : v)}>
                <SelectTrigger id="disposition-stage" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Keeps the stage</SelectItem>
                  {stageSlugs().map((stage) => (
                    <SelectItem key={stage} value={stage} description={stageText(stage).description}>
                      {stageText(stage).label}
                    </SelectItem>
                  ))}
                  {values.stage && !isCurrentStage(values.stage) && (
                    <SelectItem value={values.stage}>{stageText(values.stage).label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>
            {values.category === "compliance" && (
              <Callout tone="warning">
                A compliance outcome is do-not-call whatever its kind: the dialer never calls the lead
                again, and a call with a lead marks the lead itself do-not-call.
              </Callout>
            )}

            <Field
              label="Add the lead to"
              htmlFor="disposition-enlist"
              error={errors.enlist_campaign_id?.message}
              hint="Choosing this outcome puts the lead on that campaign's dial list, and finishes them on the campaign they were called from. Leave it at None and the outcome only moves the lead within its own campaign."
            >
              <Select
                value={values.enlist_campaign_id || NONE}
                onValueChange={(v) => set("enlist_campaign_id", v === NONE ? "" : v)}
              >
                <SelectTrigger id="disposition-enlist" invalid={Boolean(errors.enlist_campaign_id)}>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {(campaigns ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
              <div>
                <p id="disposition-note-label" className="text-xs font-medium text-text">
                  Needs a note
                </p>
                <p className="text-xs text-text-muted">
                  The rep has to write a note to choose it. On a call with a lead, the note goes out
                  with the outcome.
                </p>
              </div>
              <Switch
                checked={values.requires_note}
                onCheckedChange={(checked) => set("requires_note", checked)}
                aria-labelledby="disposition-note-label"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {disposition ? "Save changes" : "Add disposition"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function Choice({
  name, checked, onSelect, title, description,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2">
      <input
        type="radio"
        name={name}
        className="mt-0.5 accent-accent-9"
        checked={checked}
        onChange={onSelect}
      />
      <span>
        <span className="block text-xs font-medium text-text">{title}</span>
        <span className="block text-xs text-text-muted">{description}</span>
      </span>
    </label>
  );
}
